// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson

import type { Bundle } from '../data';
import { stateColour, stateName } from '../data';
import { num, safe, tipAttr, escapeHtml } from '../format';

/**
 * The honesty check on the headline. The Map and Growth views both compress
 * twenty years into two endpoints; this one refuses to. Each state is drawn as
 * the DIFFERENCE between its own index and the national index, so a panel is
 * teal in the years it beat the APS as a whole and amber in the years it lagged.
 *
 * It matters because the dispersal story is recent, not structural: Canberra
 * runs ahead of the national line for most of the series and only falls behind
 * after 2022. Presenting the 2022-25 surge as a long-run trend would be wrong,
 * and this view is what stops that reading.
 */

export function renderEras(root: HTMLElement, b: Bundle): void {
  const years = b.states.years;
  let baseYear = years[0];
  let promoted: string | null = null;

  root.innerHTML = `
    <div class="view-head">
      <h2>Who beat the average, and when</h2>
      <p>Each state indexed to its own starting size, drawn against the national line. Teal means the state grew faster than the APS as a whole that year; amber means it lagged. Click a panel to enlarge it.</p>
    </div>
    <div class="controls">
      <div class="control-group">
        <span class="control-label">Index from</span>
        <select id="era-base"></select>
      </div>
      <button type="button" class="icon-btn" id="era-clear" style="background:var(--bg-elevated);color:var(--text-secondary);border-color:var(--border-default)">Show all panels</button>
    </div>
    <div id="era-grid"></div>`;

  const sel = root.querySelector<HTMLSelectElement>('#era-base')!;
  years.slice(0, -3).forEach((y) => {
    const o = document.createElement('option');
    o.value = String(y); o.textContent = String(y);
    if (y === baseYear) o.selected = true;
    sel.appendChild(o);
  });

  function series(vals: Record<string, number | null>): (number | null)[] {
    const base = vals[String(baseYear)];
    if (!base) return years.map(() => null);
    return years.map((y) => {
      const v = vals[String(y)];
      return v === null || v === undefined ? null : (v / base) * 100;
    });
  }

  function draw(): void {
    const national: Record<string, number> = {};
    for (const y of years) {
      national[String(y)] = b.states.rows.reduce((s, r) => s + (r.values[String(y)] ?? 0), 0);
    }
    const natIdx = series(national);
    const rows = promoted ? b.states.rows.filter((r) => r.name === promoted) : b.states.rows;
    const big = !!promoted;
    const W = big ? 900 : 330;
    const H = big ? 330 : 158;
    const L = 40, R = 12, T = 22, Bm = 26;
    const iw = W - L - R, ih = H - T - Bm;

    const all = b.states.rows.flatMap((r) => series(r.values).filter((v): v is number => v !== null));
    const lo = Math.min(90, ...all), hi = Math.max(...all, 110);
    const xAt = (i: number) => L + (i / (years.length - 1)) * iw;
    const yAt = (v: number) => T + ih - ((safe(v) - lo) / (hi - lo || 1)) * ih;

    root.querySelector('#era-grid')!.innerHTML = `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(min(${big ? 900 : 300}px,100%),1fr));gap:var(--space-md)">
      ${rows.map((r) => {
        const s = series(r.values);
        const colour = stateColour(r.name);
        // The band between the state line and the national line IS the argument.
        const above: string[] = [];
        const below: string[] = [];
        for (let i = 0; i < years.length - 1; i++) {
          const a0 = s[i], a1 = s[i + 1], n0 = natIdx[i], n1 = natIdx[i + 1];
          if (a0 === null || a1 === null || n0 === null || n1 === null) continue;
          const seg = `M${xAt(i).toFixed(1)},${yAt(a0).toFixed(1)} L${xAt(i + 1).toFixed(1)},${yAt(a1).toFixed(1)} `
            + `L${xAt(i + 1).toFixed(1)},${yAt(n1).toFixed(1)} L${xAt(i).toFixed(1)},${yAt(n0).toFixed(1)} Z`;
          ((a0 + a1) / 2 >= (n0 + n1) / 2 ? above : below).push(seg);
        }
        const line = s.map((v, i) => v === null ? '' : `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join('');
        const natLine = natIdx.map((v, i) => v === null ? '' : `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join('');
        const last = s[s.length - 1];
        const hot = years.map((y, i) => {
          const v = r.values[String(y)];
          const idx = s[i], nat = natIdx[i];
          return `<rect class="bar" x="${(xAt(i) - iw / (years.length * 2)).toFixed(1)}" y="${T}" `
            + `width="${(iw / years.length).toFixed(1)}" height="${ih}" fill="transparent" `
            + `data-tip="${escapeHtml(tipAttr([
              `${stateName(r.name)} · ${y}`,
              `Headcount: ${num(v ?? null)}`,
              `Index (${baseYear} = 100): ${idx === null ? '—' : idx.toFixed(1)}`,
              `National index: ${nat === null ? '—' : nat.toFixed(1)}`,
              idx !== null && nat !== null
                ? `${idx >= nat ? 'Ahead of' : 'Behind'} the APS by ${Math.abs(idx - nat).toFixed(1)} points`
                : '',
            ]))}"><title></title></rect>`;
        }).join('');
        return `<div class="panel" style="padding:var(--space-md);cursor:pointer" data-era="${escapeHtml(r.name)}">
          <div style="display:flex;justify-content:space-between;align-items:baseline;gap:var(--space-sm)">
            <strong style="font-size:var(--font-size-sm)">${escapeHtml(stateName(r.name))}</strong>
            <span class="num" style="font-size:var(--font-size-xs);color:var(--text-secondary)">
              ${last === null ? '—' : (last >= (natIdx[natIdx.length - 1] ?? 0) ? '▲' : '▼')} ${last === null ? '' : last.toFixed(0)}
            </span>
          </div>
          <svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="${escapeHtml(stateName(r.name))} indexed growth against the national average">
            <line class="gridline" x1="${L}" y1="${yAt(100)}" x2="${W - R}" y2="${yAt(100)}" />
            <path d="${above.join(' ')}" fill="#7fd1c8" fill-opacity="0.55" stroke="none" />
            <path d="${below.join(' ')}" fill="#e0a24a" fill-opacity="0.5" stroke="none" />
            <path d="${natLine}" fill="none" stroke="#7b8ea1" stroke-width="1.3" stroke-dasharray="4 3" />
            <path d="${line}" fill="none" stroke="${colour}" stroke-width="2" />
            <text x="${L}" y="${H - 8}" font-size="13">${years[0]}</text>
            <text x="${W - R}" y="${H - 8}" font-size="13" text-anchor="end">${years[years.length - 1]}</text>
            <text x="${L - 6}" y="${yAt(100) + 3}" font-size="12" text-anchor="end">100</text>
            ${hot}
          </svg>
        </div>`;
      }).join('')}</div>`;
  }

  sel.addEventListener('change', () => { baseYear = Number(sel.value); draw(); });
  root.querySelector('#era-clear')!.addEventListener('click', () => { promoted = null; draw(); });
  root.addEventListener('click', (e) => {
    const p = (e.target as Element).closest('[data-era]');
    if (!p) return;
    const name = p.getAttribute('data-era')!;
    promoted = promoted === name ? null : name;
    draw();
  });

  draw();
}
